import {
  Module,
  VuexModule,
  Mutation,
  Action,
  getModule,
} from "vuex-module-decorators";
import store from "@/store/index";
import LocalDataService from "@/service/LocalDataService";
import ApiAiReport, {
  AiReportBatchResponse,
  AiReportDetailResponse,
  AiReportLanguage,
  AiReportQuarter,
  AiReportQuarterDetailResponse,
  AiReportTopResponse,
} from "@/api/ApiAiReport";

function resolveNurseryId(): number | null {
  const user = LocalDataService.getUser();
  if (!user) return null;
  const nurserySchool =
    user.nurserySchools.find((n) => n.isDefault === true) ??
    user.nurserySchools[0];
  return nurserySchool ? nurserySchool.nurseryId : null;
}

export interface AiReportState {
  top: AiReportTopResponse | null;
  quarterDetail: AiReportQuarterDetailResponse | null;
  detail: AiReportDetailResponse | null;
  activeBatch: AiReportBatchResponse | null;
}

@Module({ dynamic: true, store, name: "ai-report", namespaced: true })
class AiReportModule extends VuexModule implements AiReportState {
  public top: AiReportTopResponse | null = null;
  public quarterDetail: AiReportQuarterDetailResponse | null = null;
  public detail: AiReportDetailResponse | null = null;
  public activeBatch: AiReportBatchResponse | null = null;

  @Mutation
  public setTop(value: AiReportTopResponse | null) {
    this.top = value;
  }

  @Mutation
  public setQuarterDetail(value: AiReportQuarterDetailResponse | null) {
    this.quarterDetail = value;
  }

  @Mutation
  public setDetail(value: AiReportDetailResponse | null) {
    this.detail = value;
  }

  @Mutation
  public setActiveBatch(value: AiReportBatchResponse | null) {
    this.activeBatch = value;
  }

  @Action({ rawError: true })
  public async fetchTop({ businessYear }: { businessYear: number }) {
    const nurseryId = resolveNurseryId();
    if (!nurseryId) return;
    const response = await ApiAiReport.getTop({ nurseryId, businessYear });
    this.setTop(response);
  }

  @Action({ rawError: true })
  public async fetchQuarter({
    businessYear,
    quarter,
  }: {
    businessYear: number;
    quarter: AiReportQuarter;
  }) {
    const nurseryId = resolveNurseryId();
    if (!nurseryId) return;
    const response = await ApiAiReport.getQuarterDetail({
      nurseryId,
      businessYear,
      quarter,
    });
    this.setQuarterDetail(response);
  }

  @Action({ rawError: true })
  public async fetchDetail({
    businessYear,
    quarter,
    childId,
  }: {
    businessYear: number;
    quarter: AiReportQuarter;
    childId: number;
  }) {
    const nurseryId = resolveNurseryId();
    if (!nurseryId) return;
    const response = await ApiAiReport.getDetail({
      nurseryId,
      businessYear,
      quarter,
      childId,
    });
    this.setDetail(response);
  }

  @Action({ rawError: true })
  public async regenerate({
    businessYear,
    quarter,
    childId,
    language,
  }: {
    businessYear: number;
    quarter: AiReportQuarter;
    childId: number;
    language?: AiReportLanguage | null;
  }) {
    const nurseryId = resolveNurseryId();
    if (!nurseryId) return null;
    const batch = await ApiAiReport.regenerateChild({
      nurseryId,
      businessYear,
      quarter,
      childId,
      language,
    });
    this.setActiveBatch(batch);
    return batch;
  }

  @Action({ rawError: true })
  public async pollBatch({ batchId }: { batchId: number }) {
    const nurseryId = resolveNurseryId();
    if (!nurseryId) return null;
    const batch = await ApiAiReport.getBatch({ nurseryId, batchId });
    this.setActiveBatch(batch);
    return batch;
  }

  @Action({ rawError: true })
  public async updateStatus({
    businessYear,
    quarter,
    childId,
    status,
  }: {
    businessYear: number;
    quarter: AiReportQuarter;
    childId: number;
    status: "published" | "draft";
  }) {
    const nurseryId = resolveNurseryId();
    if (!nurseryId) return null;
    const response = await ApiAiReport.setStatus({
      nurseryId,
      businessYear,
      quarter,
      childId,
      status,
    });
    if (response) {
      this.setDetail(response);
    }
    return response;
  }
}

export const aiReportModule = getModule(AiReportModule);
